import React from "react";
import LegalPageLayout from "../components/LegalPageLayout";
import { LegalSection, LegalP, LegalList } from "../components/LegalContent";
import { useSeo } from "../lib/useSeo";

export default function PrivacyPage() {
  useSeo({
    title: "Debt Collector Kill Switch Privacy Policy",
    description: "Privacy Policy explaining how Debt Collector Kill Switch and TDCR Consulting, LLC collect, use, protect and disclose information.",
    canonical: "https://debtcollectorkillswitch.com/privacy",
  });

  return (
    <LegalPageLayout pageTitle="Privacy Policy" effectiveDate="July 1, 2026">
      <LegalP>
        This Privacy Policy applies to DebtCollectorKillSwitch.com and related checkout, support, and digital-product activities operated or fulfilled by TDCR Consulting, LLC. The website and Product are intended only for U.S. residents age 18 or older.
      </LegalP>

      <LegalSection heading="1. Scope and Who We Are">
        <LegalP>
          TDCR Consulting, LLC operates and fulfills the Debt Collector Kill Switch digital product. This Privacy Policy explains the categories of information we may collect, how we use and disclose it, the choices available to you, and how to contact us. It applies to DebtCollectorKillSwitch.com, related landing pages, support communications, and checkout or delivery flows associated with the Product.
        </LegalP>
      </LegalSection>

      <LegalSection heading="2. Information We Collect">
        <LegalP>Depending on how you interact with us, we may collect:</LegalP>
        <LegalList items={[
          "Contact information, such as your name, email address, mobile number, and billing contact details.",
          "Order and transaction information, such as the product purchased, amount paid, order date, transaction status, coupon or referral information, and access/delivery status.",
          "Payment-related information. Payments are handled by third-party payment processors. We generally do not receive or store your full payment-card number or security code.",
          "Support and communication information, including messages you send us and records of support requests.",
          "Information you voluntarily provide about a debt, collection account, dispute, credit report, lawsuit, or related situation when requesting support. Please do not send sensitive information that is not necessary.",
          "Device and usage information, such as IP address, browser type, device type, operating system, referring URL, pages viewed, approximate location derived from IP address, and timestamps.",
          "Cookie, analytics, advertising, and similar technology data used to operate, secure, improve, and market the website.",
          "SMS consent and opt-out records if you choose to receive text messages.",
        ]} />
      </LegalSection>

      <LegalSection heading="3. How We Use Information">
        <LegalList items={[
          "Process orders, deliver digital access, authenticate access, and provide purchased materials.",
          "Respond to questions, provide customer support, and troubleshoot access or delivery issues.",
          "Send transaction, security, service, product-access, and other communications you request or that are necessary to fulfill your order.",
          "Send SMS or marketing communications only as permitted by law and consistent with your choices.",
          "Operate, maintain, personalize, analyze, secure, and improve the website and Product.",
          "Detect fraud, abuse, unauthorized access, payment issues, security incidents, or violations of our Terms.",
          "Comply with legal obligations, lawful requests, court orders, regulatory requirements, and enforce our agreements.",
          "Measure advertising and marketing performance and, where used, deliver or evaluate interest-based advertising subject to applicable law and available opt-out rights.",
        ]} />
      </LegalSection>

      <LegalSection heading="4. How We Disclose Information">
        <LegalP>We may disclose information to:</LegalP>
        <LegalList items={[
          "Service providers that help us host the website, process payments, deliver files, send email or SMS, provide analytics, prevent fraud, provide customer support, or perform other business functions.",
          "Advertising and analytics providers when we use pixels, cookies, or similar technologies for measurement, attribution, or advertising. Depending on applicable state law, some of these disclosures may be treated as \u201csharing\u201d or a \u201csale\u201d even when no money is exchanged.",
          "Professional advisers, auditors, insurers, or legal counsel as reasonably necessary.",
          "Government authorities, regulators, courts, or other parties when required by law or reasonably necessary to protect rights, safety, security, or prevent fraud.",
          "A successor, buyer, or other party involved in a merger, financing, reorganization, sale of assets, or similar business transaction.",
        ]} />
        <LegalP>
          We do not sell your mobile number or SMS opt-in consent to third parties for their independent marketing. Text-message consent data may be shared with messaging and technology providers only as necessary to deliver messages, maintain consent records, comply with law, or provide related services.
        </LegalP>
      </LegalSection>

      <LegalSection heading="5. Payment Processing">
        <LegalP>
          Checkout may occur on TDCRConsulting.com or through a third-party payment processor. Payment processors collect and process payment information under their own privacy and security practices. We may receive transaction identifiers, billing status, limited payment details, fraud indicators, and other information necessary to confirm and support your order.
        </LegalP>
      </LegalSection>

      <LegalSection heading="6. Cookies, Analytics, and Advertising Technologies">
        <LegalP>
          We may use cookies, pixels, local storage, analytics tools, and similar technologies to remember preferences, maintain site functionality, measure traffic, understand how visitors use the site, prevent fraud, attribute conversions, and support advertising. Your browser or device may allow you to block or delete certain technologies. Blocking them may affect site functionality.
        </LegalP>
      </LegalSection>

      <LegalSection heading="7. Sensitive Information and Documents">
        <LegalP>
          The Product may concern financial and legal matters, but you should not send us sensitive information unless it is necessary for a specific support request. Before sending any document, redact Social Security numbers, full bank or credit-card numbers, passwords, dates of birth, medical information, full account numbers, and other unnecessary sensitive data. Email is not guaranteed to be a secure transmission method.
        </LegalP>
      </LegalSection>

      <LegalSection heading="8. Data Retention">
        <LegalP>
          We retain information for as long as reasonably necessary for the purposes described in this Policy, including order fulfillment, access management, customer support, accounting, fraud prevention, legal compliance, dispute resolution, and enforcement of agreements. Retention periods vary depending on the type of information and legal requirements.
        </LegalP>
      </LegalSection>

      <LegalSection heading="9. Data Security">
        <LegalP>
          We use commercially reasonable administrative, technical, and organizational measures designed to protect information. No website, email system, payment system, or storage method can be guaranteed to be completely secure, and we cannot guarantee absolute security.
        </LegalP>
      </LegalSection>

      <LegalSection heading="10. Your Choices and Privacy Rights">
        <LegalP>
          You may unsubscribe from marketing email using the unsubscribe mechanism provided in the message. You may opt out of SMS as described in the SMS &amp; Communication Terms. You may also control certain cookies through browser settings or available consent tools. Residents of certain U.S. states may have rights under applicable privacy laws, which can include rights to request access, correction, deletion, portability, or information about certain disclosures, and in some jurisdictions to opt out of targeted advertising, sale, or sharing of personal information. These rights apply only when the relevant law applies to us and to your information. To submit a privacy request, email info@debtcollectorkillswitch.com. We may need to verify your identity before completing a request.
        </LegalP>
      </LegalSection>

      <LegalSection heading="11. Do Not Track and Global Privacy Signals">
        <LegalP>
          Browser-based &ldquo;Do Not Track&rdquo; signals are not interpreted uniformly across the industry. Where applicable law requires recognition of a legally valid browser-based opt-out preference signal, we will process it as required by that law for the browser or device from which it is sent.
        </LegalP>
      </LegalSection>

      <LegalSection heading="12. Children">
        <LegalP>
          The website and Product are not intended for anyone under 18. We do not knowingly solicit or sell the Product to children. If you believe a minor has provided us personal information, contact us so we can review the request and take appropriate action.
        </LegalP>
      </LegalSection>

      <LegalSection heading="13. U.S.-Only Service">
        <LegalP>
          The website and Product are intended solely for U.S. residents located in the United States. We do not market or offer the Product to residents of other countries. If you access the website from outside the United States, you do so contrary to the intended geographic scope of the Product.
        </LegalP>
      </LegalSection>

      <LegalSection heading="14. Third-Party Websites">
        <LegalP>
          Our website may link to third-party websites or services. Their privacy practices are governed by their own policies. We are not responsible for the privacy, security, content, or practices of third parties.
        </LegalP>
      </LegalSection>

      <LegalSection heading="15. Changes to This Privacy Policy">
        <LegalP>
          We may update this Privacy Policy from time to time. The effective date shown at the top identifies the current version. We will provide any additional notice required by applicable law.
        </LegalP>
      </LegalSection>

      <LegalSection heading="16. Contact Us">
        <LegalP>
          Privacy questions and requests may be sent to info@debtcollectorkillswitch.com.
        </LegalP>
      </LegalSection>
    </LegalPageLayout>
  );
}
